import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type ReactNode } from 'react';
import { Box, Button, Flex, IconButton } from '@/components/common';
import { cn } from '@/lib/utils';

export interface PageTopBarProps {
  /** Convenience: renders a back button in `startSlot` when `startSlot` is omitted.
   *  `false` (default) — no back button.
   *  `true` — icon-only back arrow.
   *  `string` — back arrow with the provided label.
   */
  backButton?: boolean | string;
  onBack?: () => void;
  startSlot?: ReactNode;
  centerSlot?: ReactNode;
  endSlot?: ReactNode;
  className?: string;
}

/** Top bar chrome — start / center / end. Default layout keeps the center visually centered. */
export function PageTopBar({
  backButton = false,
  onBack,
  startSlot,
  centerSlot,
  endSlot,
  className,
}: PageTopBarProps) {
  const { t } = useTranslation('common');

  const hasBackButton = onBack && backButton !== false;
  const defaultStartSlot =
    hasBackButton &&
    (backButton === true ? (
      <IconButton
        type="button"
        variant="ghost"
        size="2"
        onClick={onBack}
        aria-label={t('actions.back')}
      >
        <ChevronLeft strokeWidth={2.5} aria-hidden size={24} />
      </IconButton>
    ) : (
      <Button
        type="button"
        variant="ghost"
        prefixIcon={<ChevronLeft strokeWidth={2.5} aria-hidden size={24} />}
        onClick={onBack}
      >
        {backButton}
      </Button>
    ));

  return (
    <Box width="100%" className={cn('pt-safe-area', className)}>
      <Flex asChild align="center" gap="2" width="100%" px="3" py="3">
        <header>
          <Flex flexShrink="0" flexBasis="0" justify="start" align="center">
            {startSlot ?? defaultStartSlot}
          </Flex>

          {centerSlot && (
            <Box flexGrow="1" px="1">
              {centerSlot}
            </Box>
          )}

          <Flex flexGrow="1" flexBasis="0" justify="end" align="center">
            {endSlot}
          </Flex>
        </header>
      </Flex>
    </Box>
  );
}
